import { API } from '@/config';

export type AccountResponse = {
  id: string;
  email: string;
  role_id: string;
  extra_scopes?: string[];
  email_confirmed_at?: string;
  created_at?: string;
  updated_at?: string;
};

export type RegisterRequest = {
  email: string;
  password: string;
  confirm_password: string;
};

export type RegisterResponse = {
  account: AccountResponse;
};

export type LoginRequest = {
  email: string;
  password: string;
};

export type LoginResponse = {
  token: string;
  account: AccountResponse;
};

export type MeResponse = {
  id: string;
  email: string;
  role_id: string;
  scopes?: string[];
  capabilities?: string[];
};

export type ChangePasswordRequest = {
  current_password: string;
  new_password: string;
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

export class AuthApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

export type AuthApiClient = {
  register(input: RegisterRequest): Promise<RegisterResponse>;
  login(input: LoginRequest): Promise<LoginResponse>;
  me(token: string): Promise<MeResponse>;
  changePassword(token: string, input: ChangePasswordRequest): Promise<AccountResponse>;
};

export function createAuthApiClient(baseURL = API.authBase): AuthApiClient {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');

  return {
    register(input) {
      return request<RegisterResponse>(`${normalizedBaseURL}/register`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      });
    },
    login(input) {
      return request<LoginResponse>(`${normalizedBaseURL}/login`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      });
    },
    me(token) {
      return request<MeResponse>(`${normalizedBaseURL}/me`, {
        headers: {
          ...jsonHeaders(),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    changePassword(token, input) {
      return request<AccountResponse>(`${normalizedBaseURL}/password`, {
        method: 'PATCH',
        headers: {
          ...jsonHeaders(),
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });
    },
  };
}

function jsonHeaders(): Record<string, string> {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    headers: {
      Accept: 'application/json',
      ...init?.headers,
    },
    ...init,
  });

  const body = await parseBody(response);

  if (!response.ok) {
    throw new AuthApiError(errorMessage(body, response.statusText), response.status);
  }

  if (isEnvelope<T>(body)) {
    if (body.ok === false) {
      throw new AuthApiError(errorMessage(body, response.statusText), response.status);
    }

    if ('data' in body) {
      return body.data as T;
    }
  }

  return body as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'ok' in body);
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: unknown }).error;

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback || 'request failed';
}
